import { Badge, Box, Button, Center, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import { useMayCreateContract } from "./useMayCreateContract";
import { useState } from "react";
import { FiAward, FiExternalLink, FiFileText } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ReceivedBidV2Dto,
  SourcingEventStatus,
  SourcingEventV2Dto,
  useGetEvaluationQuery,
  useListBidV2SForEventQuery,
} from "../../../../autogen/bff-api";
import { useFinalStageRound } from "../rounds/RoundSelectionContext";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { formatTimeLeft, timeLeftUntil } from "../../../../common/formatting/timeLeft";
import { useCurrentMoment } from "../../../sourcing-events/portal/utils/useCurrentMoment";
import { contractUrl } from "../contractUrl";
import { bidDocuments } from "./bidDocuments";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardDecisionHistory } from "./AwardDecisionHistory";
import { EventValueSection } from "./EventValueSection";
import { AwardLetterWorkbench } from "./AwardLetterWorkbench";
import { CreateContractFromBidModal } from "./CreateContractFromBidModal";
import { RevokeAwardModal } from "./RevokeAwardModal";
import { REVEAL_ANCHORS } from "../revealAnchor";

export const AwardOutcome = ({ event, readOnly = false }: { event: SourcingEventV2Dto; readOnly?: boolean }) => {
  const mayCreateContract = useMayCreateContract(event);
  const { t } = useTranslation();
  const finalStageRound = useFinalStageRound();
  const navigate = useNavigate();
  const displayDateWithTime = useDisplayDateWithTime();
  const { data, isLoading: areBidsLoading } = useListBidV2SForEventQuery({ eventId: event.id });
  const { data: evaluation, isError: isEvaluationError } = useGetEvaluationQuery({ eventId: event.id, round: finalStageRound?.id });
  const [contractModalBidId, setContractModalBidId] = useState<string | null>(null);
  const [isRevokeModalOpen, setRevokeModalOpen] = useState(false);

  const award = event.award;
  const bids = data?.bids ?? [];
  const winningBidIds = award?.winningBidIds ?? [];
  const winners = winningBidIds
    .map((id) => bids.find((b) => b.id === id))
    .filter((b): b is ReceivedBidV2Dto => !!b);
  const contractModalBid = winners.find((w) => w.id === contractModalBidId);
  const isDecision = event.status === SourcingEventStatus.AwardDecision;

  const standstillExpiresAt = award?.standstillExpiresAt ?? undefined;
  const standstillDispatched = standstillExpiresAt !== undefined;

  const now = useCurrentMoment();
  const standstillElapsed =
    standstillExpiresAt !== undefined && now.valueOf() >= Date.parse(standstillExpiresAt);
  const standstillTimeLeft =
    standstillExpiresAt != null ? timeLeftUntil(standstillExpiresAt, now) : null;

  return (
    <Stack spacing="6">
      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="6">
        <Text fontWeight="semibold" fontSize="sm" color="smMuted" textTransform="uppercase" mb="3">
          {t("Awarded suppliers")}
        </Text>
        <Stack spacing="2">
          {areBidsLoading ? (
            <Center py="2">
              <Spinner size="sm" color="smPrimary" />
            </Center>
          ) : winners.length > 0 ? (
            winners.map((winner) => (
              <HStack key={winner.id} spacing="3">
                <FiAward color="var(--chakra-colors-smPrimary)" />
                <Text fontWeight="semibold" fontSize="lg" color="smText">
                  {winner.ownerName || t("Unknown")}
                </Text>
                <Badge colorScheme="teal">{t("Winner")}</Badge>
              </HStack>
            ))
          ) : (
            <Text color="smMuted">{t("Unknown")}</Text>
          )}
        </Stack>
        {award?.internalNote && (
          <Box mt="4">
            <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase">
              {t("Internal note")}
            </Text>
            <Text color="smText" whiteSpace="pre-wrap">
              {award.internalNote}
            </Text>
          </Box>
        )}
      </Box>

      <EventValueSection event={event} />

      {isEvaluationError ? (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="4">
          <Text fontSize="sm" color="smError">
            {t("Could not load the evaluation.")}
          </Text>
        </Box>
      ) : (
        evaluation &&
        evaluation.bids.length > 0 && (
          <AwardBidsTable evaluation={evaluation} mode="confirmed" selectedBidIds={winningBidIds} />
        )
      )}

      <Box id={REVEAL_ANCHORS.awardLetters} sx={{ scrollMarginTop: "1rem" }}>
        <AwardLetterWorkbench event={event} />
      </Box>

      {isDecision ? (
        <Box bg="smBackgroundSecondary" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
          {!standstillDispatched ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Send the award letters to start the standstill period before confirming the award.")}
            </Text>
          ) : standstillElapsed ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("The standstill period has passed. You can now confirm the award.")}
            </Text>
          ) : (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Standstill period ends {{date}} ({{timeLeft}}). The award can be confirmed after this.", {
                date: standstillExpiresAt ? displayDateWithTime(standstillExpiresAt) : "",
                timeLeft: standstillTimeLeft ? formatTimeLeft(standstillTimeLeft, t) : "",
              })}
            </Text>
          )}
          {!readOnly && (
            <Button variant="outline" colorScheme="red" onClick={() => setRevokeModalOpen(true)}>
              {t("Change award decision")}
            </Button>
          )}
        </Box>
      ) : (
        <Stack spacing="3">
          {winners.map((winner) => {
            const { contractId } = winner;
            return (
              <Box key={winner.id} bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
                <HStack justify="space-between">
                  <HStack spacing="3">
                    <FiFileText />
                    <Text color="smText">{winner.ownerName || t("Unknown")}</Text>
                  </HStack>
                  {contractId ? (
                    <Button
                      variant="outline"
                      colorScheme="teal"
                      rightIcon={<FiExternalLink />}
                      onClick={() => navigate(contractUrl(contractId, winner.contractState, event.id))}
                    >
                      {t("Go to contract")}
                    </Button>
                  ) : readOnly ? (
                    <Text fontSize="sm" color="smMuted">
                      {t("No contract")}
                    </Text>
                  ) : mayCreateContract ? (
                    <Button colorScheme="teal" onClick={() => setContractModalBidId(winner.id)}>
                      {t("Create contract")}
                    </Button>
                  ) : null}
                </HStack>
              </Box>
            );
          })}
        </Stack>
      )}

      <AwardDecisionHistory event={event} />

      {isDecision && !readOnly && (
        <RevokeAwardModal
          eventId={event.id}
          lettersDispatched={standstillDispatched}
          isOpen={isRevokeModalOpen}
          onClose={() => setRevokeModalOpen(false)}
        />
      )}

      {contractModalBid && (
        <CreateContractFromBidModal
          eventId={event.id}
          bidId={contractModalBid.id}
          supplierName={contractModalBid.ownerName}
          defaultTitle={event.eventData.title}
          documents={bidDocuments(contractModalBid)}
          isOpen={contractModalBidId !== null}
          onClose={() => setContractModalBidId(null)}
        />
      )}
    </Stack>
  );
};
